"use client";

import { DialogTitle } from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import confetti from "canvas-confetti";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useCallback, useEffect } from "react";
import { CopyAnimatedButton } from "@/components/ui";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
} from "@/components/ui/drawer";
import { XIcon } from "@/components/ui/XIcon";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Link } from "@/i18n/navigation";
// import { AddNftToWallet } from "../AddNftToWallet";
import { WalletAddress } from "../WalletAddress";

export const ConfettiCustomShapes = () => {
  const handleClick = useCallback(() => {
    const scalar = 0.8;

    // Define custom paths based on your image’s shapes
    const triangle = confetti.shapeFromPath({
      path: "M0 10 L5 0 L10 10 Z",
    });

    const spike = confetti.shapeFromPath({
      path: "M0 10 L5 0 L6 10 Z",
    });

    const wave = confetti.shapeFromPath({
      path: "M0 5 Q2.5 0 5 5 T10 5", // simple sine-like wave
    });

    const circle = confetti.shapeFromPath({
      path: "M5 0 A5 5 0 1 0 5 10 A5 5 0 1 0 5 0 Z",
    });

    const smallDot = confetti.shapeFromPath({
      path: "M3 0 A3 3 0 1 0 3 6 A3 3 0 1 0 3 0 Z",
    });

    const shapes = [triangle, spike, wave, circle, smallDot];

    const defaults = {
      spread: 360,
      ticks: 90,
      gravity: 0.4,
      decay: 0.94,
      startVelocity: 25,
      scalar,
      colors: ["#ff5722", "#4caf50", "#ffc107"],
      shapes,
    };

    const shoot = () => {
      confetti({
        ...defaults,
        particleCount: 20,
      });

      confetti({
        ...defaults,
        particleCount: 15,
        startVelocity: 5,
      });
    };

    // small burst sequence
    setTimeout(shoot, 0);
    setTimeout(shoot, 200);
    setTimeout(shoot, 400);
  }, []);

  useEffect(() => {
    handleClick();
    handleClick();
    handleClick();
  }, [handleClick]);

  return null;
};

/**
 * skill-ack: bug fix only, reusing existing Dialog/Drawer primitives (the
 * project's established responsive-modal pattern, e.g.
 * features/staking/ResponsiveModal.tsx), no new UI/components.
 *
 * Renders as a Drawer on mobile, not a Dialog: Drawer content uses z-110
 * while Dialog uses z-50 (see components/ui/drawer.tsx and dialog.tsx). This
 * success screen can open on top of a purchase flow presented in a Drawer,
 * so a plain Dialog here rendered behind it and was unusable.
 */
export const NFTCardSuccess = ({
  onOpenChange,
  open,
  tokenId,
  nftCollectionAddress,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tokenId: number | null;
  nftCollectionAddress: string | null;
}) => {
  const t = useTranslations("nft");
  const isMobile = useMediaQuery("(max-width: 767px)");

  const body = (
    <div className="text-center text-text-primary">
      <h2 className="text-lg font-bold">{t("success.title")}</h2>
      <p className="text-sm text-muted-foreground line-clamp-3">
        {t("success.description")}
      </p>

      {/* Transaction Details Box */}
      {(tokenId || nftCollectionAddress) && (
        <div className="rounded-lg p-3 space-y-2 mt-4 border border-stroke-light">
          {tokenId && (
            <div className="flex items-center gap-2 text-sm text-text-primary justify-between">
              <span className="text-text-secondary">
                {t("success.productId")}
              </span>
              <span className="flex items-center gap-1">
                {tokenId}
                <CopyAnimatedButton text={tokenId.toString()} />
              </span>
            </div>
          )}
          {nftCollectionAddress && (
            <div className="flex items-center gap-2 text-sm text-text-primary justify-between">
              <span className="text-text-secondary">
                {t("success.contractAddress")}
              </span>
              <WalletAddress address={nftCollectionAddress} />
            </div>
          )}

          <Button
            colors="secondary"
            variant="light"
            className="w-full mt-4 underline"
            asChild
          >
            <Link href="/history" className="text-text-primary">
              {t("success.viewHistory")}
            </Link>
          </Button>
        </div>
      )}

      <ConfettiCustomShapes />
    </div>
  );

  const image = (
    <Image
      src={"/images/ui/nft-success-purchase.webp"}
      alt="success"
      width={128}
      height={128}
      className="mx-auto rotate-[16.99] -mt-12 object-cover"
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="space-y-4 px-9 pb-8">
          <VisuallyHidden>
            <DialogTitle>{t("success.title")}</DialogTitle>
          </VisuallyHidden>
          <DrawerHeader className="flex-row items-center justify-end px-0">
            <DrawerClose asChild>
              <Button size={"icon"} colors="secondary" variant={"light"}>
                <XIcon size={20} className="text-text-primary" />
              </Button>
            </DrawerClose>
          </DrawerHeader>
          {image}
          <DrawerDescription asChild className="px-0 space-y-4 py-2">
            {body}
          </DrawerDescription>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs space-y-4 px-9">
        <VisuallyHidden>
          <DialogTitle>{t("success.title")}</DialogTitle>
        </VisuallyHidden>
        <DialogHeader className="flex-row items-center justify-end">
          <DialogClose asChild>
            <Button size={"icon"} colors="secondary" variant={"light"}>
              <XIcon size={20} className="text-text-primary" />
            </Button>
          </DialogClose>
        </DialogHeader>
        {image}
        <DialogDescription asChild className="px-0 space-y-4 py-2">
          {body}
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};
